import type { ReactNode } from 'react'
import cn from 'clsx'
import {
    Avatar,
    AvatarFallback,
    BurgerMenuIcon,
    Button,
    buttonClassNames,
    CloseIcon,
    Link,
    UploadIcon,
    VisuallyHidden,
} from '@ds/components'
import { HEADER_HAMBURGER_ID, MOBILE_DRAWER_ID } from '../../constants/mobileDrawer'
import { UserMenu } from '../user-menu'
import type { THeaderLabels, TMenuItemView, TSiteHeaderUser } from '../types'
import styles from './HeaderActions.module.css'

type THeaderActionsProps = {
    isAuthenticated: boolean
    user: TSiteHeaderUser
    userMenuItems: TMenuItemView[]
    labels: THeaderLabels
    search: ReactNode
    showTryFree: boolean
    isMobileMenuOpen: boolean
    isUserMenuDropdown: boolean
    tryFreeHref: string
    onSignIn: () => void
    onToggleMobileMenu: () => void
    onOpenAccountPanel: () => void
    onLogout: () => void
    onUpload: () => void
    onTryFree: () => void
}

export const HeaderActions = ({
    isAuthenticated,
    user,
    userMenuItems,
    labels,
    search,
    showTryFree,
    isMobileMenuOpen,
    isUserMenuDropdown,
    tryFreeHref,
    onSignIn,
    onToggleMobileMenu,
    onOpenAccountPanel,
    onLogout,
    onUpload,
    onTryFree,
}: THeaderActionsProps) => {
    const BurgerIcon = isMobileMenuOpen ? CloseIcon : BurgerMenuIcon

    return (
        <div className={styles.actions}>
            {search}

            <Button
                variant="secondary"
                size="small"
                type={undefined}
                LeftIcon={UploadIcon}
                className={styles.uploadButton}
                onClick={onUpload}
            >
                {labels.upload}
            </Button>

            {showTryFree ? (
                <Link
                    variant="primary"
                    href={tryFreeHref}
                    className={cn(
                        buttonClassNames({ variant: 'primary', size: 'small' }),
                        styles.tryFree,
                    )}
                    onClick={onTryFree}
                >
                    {labels.tryFree}
                </Link>
            ) : null}

            {isAuthenticated ? (
                <UserMenu
                    user={user}
                    items={userMenuItems}
                    labels={labels}
                    isDropdown={isUserMenuDropdown}
                    onLogout={onLogout}
                    onOpenAccountPanel={onOpenAccountPanel}
                />
            ) : (
                // audio-web clicks the avatar itself rather than wrapping it in a link, so the
                // header serves one `<a>` fewer; keyboard reach goes with it, and that is the
                // reference's behaviour.
                <Avatar
                    className={cn(styles.avatar, styles.guestAvatar)}
                    theme="experiment"
                    onClick={onSignIn}
                >
                    <AvatarFallback theme="experiment" />
                </Avatar>
            )}

            <button
                type="button"
                id={HEADER_HAMBURGER_ID}
                className={cn(styles.iconButton, styles.hamburger)}
                aria-expanded={isMobileMenuOpen}
                aria-controls={MOBILE_DRAWER_ID}
                onClick={onToggleMobileMenu}
            >
                <BurgerIcon />
                <VisuallyHidden>{labels.openMenu}</VisuallyHidden>
            </button>
        </div>
    )
}
